
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { BookOpen, ShoppingCart, User as UserIcon, LogOut, Menu, Shield, Truck, X, Search, MessageCircle, Loader2, Settings, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from '@/components/ui/badge';
import CategoryMenu from '@/components/CategoryMenu';
import { logger } from '@/utils/logger';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface AuthUser {
  user_metadata?: {
    name?: string;
    full_name?: string;
  };
  email?: string;
}

const getUserDisplayName = (user: AuthUser): string => {
  const raw = user?.user_metadata?.name
    ?? user?.user_metadata?.full_name
    ?? user?.email?.split('@')[0]
    ?? 'User';
  // Strip any HTML tags (e.g. from XSS-injected names)
  return raw.replace(/<[^>]*>/g, '').trim() || 'User';
};

const Navbar = () => {
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const { user, currentRole, availableRoles, logout, switchRole } = useAuth();
  const { getTotalItems } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // Track the user's recent activity context (buyer vs seller) so the
  // Dashboard link lands them on the workspace matching what they were doing.
  // Seller activity = managing listings/settings/shipping. Everything else
  // (browsing, cart, checkout, orders, account) is buyer context.
  const SELLER_PATH_PREFIXES = ['/sell', '/edit-book', '/seller-settings', '/shipping'];
  const isSellerContextPath = (path: string) =>
    SELLER_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));

  useEffect(() => {
    if (!user) return;
    try {
      if (isSellerContextPath(location.pathname)) {
        sessionStorage.setItem('lastActivityContext', 'seller');
      } else if (
        location.pathname === '/' ||
        location.pathname.startsWith('/book/') ||
        location.pathname.startsWith('/category/') ||
        location.pathname === '/books' ||
        location.pathname === '/search' ||
        location.pathname === '/cart' ||
        location.pathname === '/checkout' ||
        location.pathname.startsWith('/order')
      ) {
        sessionStorage.setItem('lastActivityContext', 'buyer');
      }
    } catch {
      /* sessionStorage unavailable — fall back to current role */
    }
  }, [location.pathname, user]);

  const resolveDashboardRole = (): 'buyer' | 'seller' | null => {
    let intent: 'buyer' | 'seller' | null = null;
    if (isSellerContextPath(location.pathname)) intent = 'seller';
    if (!intent) {
      try {
        const stored = sessionStorage.getItem('lastActivityContext');
        if (stored === 'seller' || stored === 'buyer') intent = stored;
      } catch {
        /* ignore */
      }
    }
    return intent;
  };

  const handleDashboardClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    target: string,
  ) => {
    // Admins keep their role; users with only one role have nothing to switch.
    if (currentRole === 'admin' || availableRoles.length < 2) return;
    const intent = resolveDashboardRole();
    if (!intent || intent === currentRole) return;
    if (!availableRoles.includes(intent)) return;
    e.preventDefault();
    try {
      await switchRole(intent);
    } catch (error) {
      logger.error('[Navbar] Dashboard role switch failed', error);
    }
    navigate(target);
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      // Full reload clears all in-memory state/caches for a clean slate
      window.location.href = '/';
    } catch (error) {
      logger.error('[Navbar] Logout failed', error);
      setLoggingOut(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Defense-in-depth: trim, strip angle brackets / control chars, and clamp length
    // before sending the query to the route. Server-side sanitization still applies.
    const sanitized = searchQuery
      .trim()
      .replace(/[<>]/g, '')
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, '')
      .slice(0, 200);
    if (sanitized) {
      navigate(`/search?search=${encodeURIComponent(sanitized)}`);
      setSearchQuery('');
      setShowMobileSearch(false);
    }
  };

  const totalItems = getTotalItems();

  return (
    <nav className="bg-background shadow-sm border-b relative z-50">
      {/* Mobile Category Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transform transition-transform duration-300 ease-in-out
        ${showMobileCategories ? 'translate-x-0' : '-translate-x-full'}
        md:hidden overflow-y-auto
      `}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">Categories</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMobileCategories(false)}
              className="h-11 w-11"
              aria-label="Close categories"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <CategoryMenu isMobile />
        </div>
      </div>
      
      {/* Overlay for mobile sidebar */}
      {showMobileCategories && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setShowMobileCategories(false)}
          aria-hidden="true"
        />
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo with Mobile Category Button */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMobileCategories(true)}
              className="md:hidden inline-flex items-center justify-center min-h-11 min-w-11 p-2 text-primary/70 hover:text-primary transition-colors"
              aria-label="Open categories"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <Link to="/" className="flex items-center whitespace-nowrap">
              <BookOpen className="h-6 w-6 md:h-8 md:w-8 text-accent mr-1 md:mr-2 flex-shrink-0" />
              <span className="text-lg md:text-2xl font-bold text-primary">Twice Read</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <CategoryMenu />
          </div>

          {/* Right side - Search, Cart, User menu */}
          <div className="flex items-center space-x-1 md:space-x-4">
            {/* Mobile search toggle */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="md:hidden h-11 w-11 text-muted-foreground hover:text-primary"
              onClick={() => setShowMobileSearch((v) => !v)}
              aria-label="Toggle search"
              aria-expanded={showMobileSearch}
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* Desktop search field */}
            <form onSubmit={handleSearch} className="relative hidden md:block" role="search">
              <Input
                type="search"
                placeholder="Search books..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 h-9 pr-8 text-sm"
                aria-label="Search books"
              />
               <Button
                type="submit"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-9 w-9 text-muted-foreground hover:text-primary transition-colors"
                aria-label="Submit search"
              >
                <Search className="h-4 w-4" />
              </Button>
            </form>

            {/* Cart Icon — available to guests too (guest cart persists in localStorage) */}
            <Link to="/cart" className="relative inline-flex items-center justify-center min-h-11 min-w-11 p-2 text-muted-foreground hover:text-accent transition-colors" aria-label={`Shopping cart${totalItems > 0 ? `, ${totalItems} items` : ''}`}>
              <ShoppingCart className="h-6 w-6" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Link>

            {/* User Authentication */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" aria-label="User menu">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src="" alt={getUserDisplayName(user as AuthUser)} />
                      <AvatarFallback className="bg-accent text-accent-foreground">
                        {getUserDisplayName(user as AuthUser)?.charAt(0)?.toUpperCase() ?? 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border shadow-lg z-50" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{getUserDisplayName(user as AuthUser)}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                      <Badge variant="secondary" className="w-fit mt-1">
                        {availableRoles && availableRoles.length > 0 ? availableRoles.join(', ') : (currentRole ?? 'buyer')}
                      </Badge>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer" onClick={(e) => handleDashboardClick(e, '/dashboard')}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard?tab=messages" className="cursor-pointer" onClick={(e) => handleDashboardClick(e, '/dashboard?tab=messages')}>
                      <MessageCircle className="mr-2 h-4 w-4" />
                      <span>Messages</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer">
                      <UserIcon className="mr-2 h-4 w-4" />
                      <span>Account</span>
                    </Link>
                  </DropdownMenuItem>
                  {currentRole === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/shipping" className="cursor-pointer">
                        <Truck className="mr-2 h-4 w-4" />
                        <span>Shipping Tools</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {currentRole === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer">
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Admin Panel</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer" disabled={loggingOut}>
                    {loggingOut ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <LogOut className="mr-2 h-4 w-4" />
                    )}
                    <span>{loggingOut ? 'Logging out...' : 'Log out'}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/login">Log in</Link>
              </Button>
            )}

          </div>
        </div>

        {/* Mobile expandable search */}
        {showMobileSearch && (
          <div className="md:hidden pb-3">
            <form onSubmit={handleSearch} className="relative" role="search">
              <Input
                type="search"
                autoFocus
                placeholder="Search books, authors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pr-10 text-sm"
                aria-label="Search books"
              />
              <Button
                type="submit"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-10 w-10 text-muted-foreground"
                aria-label="Submit search"
              >
                <Search className="h-4 w-4" />
              </Button>
            </form>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
